import { Auth } from '@aws-amplify/auth';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Logger } from '../../../utils/logger';
import { fetchInvokeUrl, parseStreamEvent, ServerSentEventParser } from './ariaChatClient';
import { ARIA_CHAT_NAMESPACE, ARIA_CHAT_SESSION_HEADER, MAX_QUERY_LENGTH } from './ariaChatConstants';

/**
 * Custom hook for streaming chat responses from AgentCore Runtime.
 */
export const useChatStream = (sessionId: string, language: string) => {
    const [isStreaming, setIsStreaming] = useState(false);
    const abortControllerRef = useRef<AbortController | null>(null);
    const { t } = useTranslation(ARIA_CHAT_NAMESPACE);

    const sendMessage = useCallback(
        async (
            query: string,
            onToken: (token: string) => void,
            onComplete: () => void,
            onError: (error: string) => void
        ) => {
            if (!query.trim()) {
                return;
            }
            if (query.length > MAX_QUERY_LENGTH) {
                onError(t('tooLong-errorMessage', { maxLength: MAX_QUERY_LENGTH }));
                return;
            }

            setIsStreaming(true);
            abortControllerRef.current = new AbortController();

            try {
                // The runtime's JWT authorizer checks the client_id claim, which Cognito only puts in access tokens.
                const [session, invokeUrl] = await Promise.all([Auth.currentSession(), fetchInvokeUrl()]);
                const token = session.getAccessToken().getJwtToken();

                const response = await fetch(invokeUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                        [ARIA_CHAT_SESSION_HEADER]: sessionId,
                    },
                    body: JSON.stringify({
                        prompt: query,
                        language,
                    }),
                    signal: abortControllerRef.current.signal,
                });

                if (!response.ok) {
                    if (response.status === 429) {
                        onError(t('rateLimited-errorMessage'));
                    } else {
                        onError(t('unavailable-errorMessage'));
                    }
                    return;
                }

                const reader = response.body?.getReader();
                if (!reader) {
                    onError(t('connectionLost-errorMessage'));
                    return;
                }

                const decoder = new TextDecoder();
                const parser = new ServerSentEventParser();
                // Returns the runtime's error message if the stream reported one, otherwise undefined.
                const processStream = async (): Promise<string | undefined> => {
                    const result = await reader.read();
                    if (result.done) return undefined;
                    if (result.value) {
                        const events = parser
                            .push(decoder.decode(result.value, { stream: true }))
                            .map(parseStreamEvent);
                        const errorIndex = events.findIndex((event) => event?.type === 'error');
                        const textEvents = errorIndex === -1 ? events : events.slice(0, errorIndex);
                        textEvents.forEach((event) => {
                            if (event?.type === 'text') {
                                onToken(event.text);
                            } else if (event?.type === 'break') {
                                // Separate consecutive model messages so their text doesn't run together
                                onToken('\n\n');
                            }
                        });
                        const errorEvent = events[errorIndex];
                        if (errorEvent?.type === 'error') {
                            return errorEvent.message;
                        }
                    }
                    return processStream();
                };
                const streamError = await processStream();

                if (streamError) {
                    reader.cancel();
                    onError(streamError);
                    return;
                }
                onComplete();
            } catch (error: any) {
                if (error.name === 'AbortError') {
                    return;
                }
                Logger.error('Aria chat error', error);
                onError(t('connectionLost-errorMessage'));
            } finally {
                setIsStreaming(false);
                setTimeout(() => {
                    const el = document.getElementById('aria-chat-input');
                    if (el) el.focus();
                }, 50);
            }
        },
        [sessionId, language, t]
    );

    const cancelStream = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
    }, []);

    return { sendMessage, cancelStream, isStreaming };
};
